import { SvelteDate } from 'svelte/reactivity';
import { OverlappingJobs } from './overlappingJobs.svelte';
import type { Job, JobPre, RelatedJobMap, SchedularVehicle, SchedularVehicles, ScheduledJob, WeekendOverride } from './types';
import type { ZoomRangeWithBlocks } from './zoom2.svelte';

export const getJobEnd = (job: ScheduledJob) => {
    const duration = job.duration;
    return new SvelteDate(job.start.getTime() + duration * 1000);
};

export const getJobDisplayName = (job: Job) => job.title;

export const formatSecToDisplay = (sec: number) => {
    const date = new Date();
    date.setSeconds(sec);
    return date.toISOString().slice(11, 19);
};

let lastHue = 0;
export const getNextClr = () => `hsl(${(lastHue += 135)}, 80, 80)`;

export const getUserTopOffset: (vehicles: SchedularVehicles, vehicleId: string, overlappingJobs: OverlappingJobs) => number | null = (
    vehicles,
    vehicleId,
    overlappingJobs,
) => {
    let carry = 0;
    for (const vehicle of vehicles) {
        if (vehicle.id === vehicleId) {
            return carry;
        }
        carry += getOverlappingHeight(vehicle, overlappingJobs);
    }
    return null;
};

export const getOverlappingHeight: (vehicle: SchedularVehicle, overlappingJobs: OverlappingJobs) => number = (site, overlappingJobs) => {
    const overlappingJobsForSite = overlappingJobs[site.id];
    if (!overlappingJobsForSite) {
        return 1;
    }
    return Math.max(1, overlappingJobsForSite.maxLaneCount);
};

export const getUserFromTop: (vehicles: SchedularVehicles, top: number, overlappingJobs: OverlappingJobs) => SchedularVehicle | null = (
    vehicles,
    top,
    overlappingJobs,
) => {
    for (const vehicle of vehicles) {
        top -= getOverlappingHeight(vehicle, overlappingJobs);
        if (top <= -1) return vehicle;
    }
    return null;
};

export const itemRelatedToSelect = (selectedItem: Job, otherItem: Job, relatedJobsMap: RelatedJobMap) => {
    if (selectedItem == null || selectedItem === otherItem) return true;
    const selectedJobIds = new Set([selectedItem.id]);
    for (const selJobId of selectedJobIds) {
        const relIds = relatedJobsMap.get(selJobId);
        if (relIds && relIds.has(otherItem.id)) return true;
    }
    return false;
};

export const jobIsInSearchStr = (search: string, job: Job) => {
    if (search === '') return true;
    const searchLower = search.toLowerCase();

    // Check if search matches job ID (for highlighted assignments)
    if (job.id && job.id.toString().toLowerCase() === searchLower) {
        return true;
    }

    // Check if search is in site name (original functionality)
    return (job.site_name ?? '').toLowerCase().includes(searchLower);
};

/**
 * Returns the date of the mouse cursor
 */
export const getMouseDate = (mouseX: number, zoom: ZoomRangeWithBlocks, contentDiv: HTMLDivElement, sitesWidth: number) => {
    const boundingRect = contentDiv.getBoundingClientRect();
    const xScrollPx = mouseX - boundingRect.x + contentDiv.scrollLeft - sitesWidth;
    const xScrollMs = xScrollPx * (zoom.zoomRangePreset.cellMs / zoom.cellPx);
    const mouseDateMs = zoom.blocks[0].start.getTime() + xScrollMs;
    return new SvelteDate(mouseDateMs);
};

export const roundDate = (date: Date) => {
    const roundedDateMs = Math.round(date.getTime() / (60 * 1000)) * 60 * 1000;
    return new SvelteDate(roundedDateMs);
};

export const getModifiedJobsCount = (jobs: Map<string, JobPre>, siteVisits?: Map<string, { modified: boolean }>) => {
    let count = 0;
    for (const job of jobs.values()) {
        if (job.modified) count++;
    }
    if (siteVisits) {
        for (const siteVisit of siteVisits.values()) {
            if (siteVisit.modified) count++;
        }
    }
    return count;
};

export type WorkingSegment = { start: Date; durationSec: number };

/**
 * Local-TZ YYYY-MM-DD for the Saturday that anchors the weekend that `date`
 * belongs to. Returns null for weekdays.
 */
export const weekendAnchorISO = (date: Date): string | null => {
    const day = date.getDay();
    if (day !== 0 && day !== 6) return null;
    const saturday = new Date(date);
    if (day === 0) saturday.setDate(saturday.getDate() - 1);
    saturday.setHours(0, 0, 0, 0);
    const y = saturday.getFullYear();
    const m = String(saturday.getMonth() + 1).padStart(2, '0');
    const d = String(saturday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const buildOverrideMap = (overrides?: WeekendOverride[]): Map<string, WeekendOverride> => {
    const map = new Map<string, WeekendOverride>();
    for (const o of overrides ?? []) {
        if (o?.weekend_start) map.set(o.weekend_start, o);
    }
    return map;
};

/**
 * Split a (start, durationSec) range into segments, masking out any weekend
 * day that is NOT explicitly opted-in via a per-weekend override. Ranges that
 * just touch a masked block at one edge (start/end-on-weekend, or entirely on
 * a weekend) are returned as a single un-split segment so the scheduler can
 * still place edge-case jobs.
 */
export const splitOnWeekends = (
    start: Date,
    durationSec: number,
    overrides?: WeekendOverride[],
): WorkingSegment[] => {
    if (durationSec <= 0) return [];

    const whole: WorkingSegment[] = [{ start: new SvelteDate(start.getTime()), durationSec }];
    const overrideMap = buildOverrideMap(overrides);
    const endMs = start.getTime() + durationSec * 1000;

    const isMaskedAt = (cursor: Date): boolean => {
        const day = cursor.getDay();
        if (day !== 0 && day !== 6) return false;
        const anchor = weekendAnchorISO(cursor);
        const override = anchor !== null ? overrideMap.get(anchor) : undefined;
        if (day === 6) return !(override?.include_saturday ?? false);
        return !(override?.include_sunday ?? false);
    };

    const stripped: WorkingSegment[] = [];
    let cursorMs = start.getTime();
    let segmentStartMs: number | null = null;

    while (cursorMs < endMs) {
        const cursor = new Date(cursorMs);

        const nextMidnight = new Date(cursor);
        nextMidnight.setHours(24, 0, 0, 0);
        const dayBoundaryMs = Math.min(endMs, nextMidnight.getTime());

        if (isMaskedAt(cursor)) {
            if (segmentStartMs !== null) {
                stripped.push({
                    start: new SvelteDate(segmentStartMs),
                    durationSec: (cursorMs - segmentStartMs) / 1000,
                });
                segmentStartMs = null;
            }
        } else if (segmentStartMs === null) {
            segmentStartMs = cursorMs;
        }

        cursorMs = dayBoundaryMs;
    }

    if (segmentStartMs !== null) {
        stripped.push({
            start: new SvelteDate(segmentStartMs),
            durationSec: (cursorMs - segmentStartMs) / 1000,
        });
    }

    return stripped.length >= 2 ? stripped : whole;
};

/**
 * Walk every calendar day a (start, durationSec) range touches and emit one
 * weekend anchor (YYYY-MM-DD Saturday) per weekend it covers. Used by the
 * edit panel to know how many weekend rows to show.
 */
export const weekendsCoveredBy = (start: Date, durationSec: number): string[] => {
    if (durationSec <= 0) return [];
    const endMs = start.getTime() + durationSec * 1000;
    const seen: string[] = [];
    const seenSet = new Set<string>();
    let cursorMs = start.getTime();

    while (cursorMs < endMs) {
        const cursor = new Date(cursorMs);
        const anchor = weekendAnchorISO(cursor);
        if (anchor !== null && !seenSet.has(anchor)) {
            seen.push(anchor);
            seenSet.add(anchor);
        }
        const nextMidnight = new Date(cursor);
        nextMidnight.setHours(24, 0, 0, 0);
        cursorMs = nextMidnight.getTime();
    }

    return seen;
};
